import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { TradingAccount } from '@/api/trading-accounts/use-trading-accounts';
import { useDebouncedCalculation } from '@/hooks/useDebouncedCalculation';
import { getDefaultLeverage, getOrderLeverage, type InstrumentType } from '@/utils/leverageOptions';
import { formatInstrumentPrice } from '@/utils/formatters';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';

interface OrderFormProps {
    selectedPair: string;
    selectedInstrumentId?: string;
    realTimePrice?: { bid: number; ask: number; symbol?: string };
    selectedAccount?: TradingAccount | null;
    onOrderSuccess?: () => void;
    isPricesLoading?: boolean;
    pricesError?: string | null;
    instrumentType?: InstrumentType;
    instrumentData?: {
        min_lot: number;
        max_lot: number;
        max_leverage: number;
        leverage: number;
    };
    onOpenDialog: (direction: 'buy' | 'sell') => void;
}

interface OrderFormData {
    volume: string;
    price: string;
    stopLoss: string;
    takeProfit: string;
    orderType: 'market' | 'limit' | 'stop' | 'stop_limit';
    stopType: string;
}

export const OrderForm = ({
    selectedPair,
    selectedInstrumentId,
    realTimePrice,
    selectedAccount,
    isPricesLoading = false,
    pricesError = null,
    instrumentType = 'forex',
    instrumentData,
    onOpenDialog,
}: OrderFormProps) => {
    const { t } = useTranslation('trading');
    // Get instrument limits for validation
    const minLot = instrumentData?.min_lot || 0.01;
    const instrumentMaxLeverage =
        instrumentData?.max_leverage ||
        instrumentData?.leverage ||
        getDefaultLeverage(instrumentType);
    const accountLeverage = selectedAccount?.leverage ?? instrumentMaxLeverage;
    const orderLeverage = getOrderLeverage(instrumentMaxLeverage, accountLeverage);

    // Use react-hook-form for form management
    const { watch } = useForm<OrderFormData>({
        defaultValues: {
            volume: minLot.toString(),
            price: '',
            stopLoss: '',
            takeProfit: '',
            orderType: 'market',
            stopType: 'no',
        },
        mode: 'onChange',
    });

    // Watch form values for calculations
    const watchedValues = watch();
    const { volume, price, orderType } = watchedValues;

    // Use custom hook for debounced calculations
    const { calculateOrder, clearCalculation } = useDebouncedCalculation();

    // Trigger calculation when inputs change
    React.useEffect(() => {
        if (!selectedInstrumentId || !volume || !selectedAccount) {
            clearCalculation();
            return;
        }

        const lotSize = parseFloat(volume);

        if (isNaN(lotSize) || lotSize <= 0) {
            clearCalculation();
            return;
        }

        let priceValue: number | undefined;
        if (orderType === 'limit' && price) {
            const parsedPrice = parseFloat(price);
            if (!isNaN(parsedPrice) && parsedPrice > 0) {
                priceValue = parsedPrice;
            }
        }

        calculateOrder({
            instrument_id: selectedInstrumentId,
            trading_account_id: selectedAccount.id,
            side: 'buy',
            lot_size: lotSize,
            leverage: orderLeverage,
            price: priceValue,
        });
    }, [
        selectedInstrumentId,
        volume,
        selectedAccount,
        orderType,
        orderLeverage,
        calculateOrder,
        clearCalculation,
        price,
    ]);

    return (
        <Card className="bg-background border-border shadow-sm">
            <CardContent className="space-y-3 p-4">
                {/* Header */}
                <div className="text-center mb-2">
                    <p className="text-sm text-muted-foreground">{selectedPair}</p>
                </div>

                {/* Buy/Sell Buttons with Prices */}
                <div className="grid grid-cols-2 gap-3">
                    <Button
                        type="button"
                        onClick={() => onOpenDialog('buy')}
                        disabled={isPricesLoading}
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white h-12 font-semibold transition-all duration-200 hover:scale-105"
                    >
                        <div className="flex flex-col items-center space-y-1">
                            <div className="flex items-center space-x-2">
                                <TrendingUp className="h-5 w-5" />
                                <span>{t('buy')}</span>
                            </div>
                            {realTimePrice && realTimePrice.ask ? (
                                <PriceDisplayWithFlash
                                    price={realTimePrice.ask}
                                    className="text-xs font-mono opacity-90"
                                    formatPrice={p =>
                                        formatInstrumentPrice(
                                            p,
                                            realTimePrice.symbol
                                                ? { symbol: realTimePrice.symbol }
                                                : undefined
                                        )
                                    }
                                />
                            ) : (
                                <div className="text-xs opacity-70">
                                    {isPricesLoading ? t('loadingPrice') : '--'}
                                </div>
                            )}
                        </div>
                    </Button>
                    <Button
                        type="button"
                        onClick={() => onOpenDialog('sell')}
                        disabled={isPricesLoading}
                        size="lg"
                        className="bg-red-600 hover:bg-red-700 text-white h-12 font-semibold transition-all duration-200 hover:scale-105"
                    >
                        <div className="flex flex-col items-center space-y-1">
                            <div className="flex items-center space-x-2">
                                <TrendingDown className="h-5 w-5" />
                                <span>{t('sell')}</span>
                            </div>
                            {realTimePrice && realTimePrice.bid ? (
                                <PriceDisplayWithFlash
                                    price={realTimePrice.bid}
                                    className="text-xs font-mono opacity-90"
                                    formatPrice={p =>
                                        formatInstrumentPrice(
                                            p,
                                            realTimePrice.symbol
                                                ? { symbol: realTimePrice.symbol }
                                                : undefined
                                        )
                                    }
                                />
                            ) : (
                                <div className="text-xs opacity-70">
                                    {isPricesLoading ? t('loadingPrice') : '--'}
                                </div>
                            )}
                        </div>
                    </Button>
                </div>

                {/* Error display */}
                {pricesError && (
                    <div className="text-center text-red-500 text-sm bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg border border-red-500/20">
                        {t('priceDataUnavailable')}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
